export type CommonResponse<T> = {
  statusCode: number
  success: boolean
  message: string
  result: T | null
}

export class ApiError<T = unknown> extends Error {
  status: number
  payload?: T
  constructor(status: number, message: string, payload?: T) {
    super(message)
    this.status = status
    this.payload = payload
  }
}

import { OfflineHttpAdapter } from "../offline/adapter"
import { OfflineRequestQueuedError } from "../offline/errors"
import { reportBackendFailure, reportBackendHealthy } from "../system/backendHealth"

export class HttpClient {
  private offlineAdapter?: OfflineHttpAdapter
  constructor(private baseUrl: string, private getToken?: () => string | null, offlineAdapter?: OfflineHttpAdapter) {
    this.offlineAdapter = offlineAdapter
  }

  setOfflineAdapter(adapter?: OfflineHttpAdapter) {
    this.offlineAdapter = adapter
  }

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    let body = init.body as BodyInit | undefined
    const method = (init.method || "GET").toUpperCase()
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...(init.headers as Record<string, string> | undefined),
    }
    const token = this.getToken?.()
    if (token) {
      const normalized = token.trim()
      headers.Authorization = /^bearer\s/i.test(normalized) ? normalized : `Bearer ${normalized}`
    }

    const isFormData = typeof FormData !== "undefined" && body instanceof FormData
    const isBlob = body instanceof Blob
    const isArrayBuffer = body instanceof ArrayBuffer || ArrayBuffer.isView(body as ArrayBufferView)

    if (isFormData) {
      delete headers["Content-Type"]
    } else if (body !== undefined && body !== null) {
      if (isBlob) {
        if (!headers["Content-Type"]) {
          const blob = body as Blob
          headers["Content-Type"] = blob.type || "application/octet-stream"
        }
      } else if (isArrayBuffer) {
        headers["Content-Type"] = headers["Content-Type"] || "application/octet-stream"
      } else if (typeof body === "string") {
        headers["Content-Type"] = headers["Content-Type"] || "application/json"
      } else {
        headers["Content-Type"] = headers["Content-Type"] || "application/json"
        body = JSON.stringify(body) as BodyInit
      }
    } else {
      body = undefined
    }

    let finalInit: RequestInit = { ...init, headers, body, method }
    const requestContext = {
      path,
      url: this.baseUrl + path,
      method,
      requestInit: finalInit,
    }

    if (this.offlineAdapter) {
      const decision = await this.offlineAdapter.prepare<T>(requestContext)
      if (decision.kind === "cached") {
        return decision.data
      }
      if (decision.kind === "queued") {
        throw new OfflineRequestQueuedError(decision.queueEntry)
      }
      if (decision.kind === "network" && decision.requestInit) {
        finalInit = { ...decision.requestInit }
        if (!finalInit.method) {
          finalInit.method = method
        }
        requestContext.requestInit = finalInit
      }
    }

    let res: Response
    try {
      res = await fetch(requestContext.url, finalInit)
    } catch (error) {
      reportBackendFailure({ message: (error as Error)?.message })
      if (this.offlineAdapter) {
        const fallback = await this.offlineAdapter.onError<T>(requestContext, error)
        if (fallback !== undefined) {
          return fallback
        }
      }
      throw error
    }

    const markBackendStatusFromResponse = () => {
      // Treat only true service-unavailable cases as backend failure to avoid noisy offline prompts on application errors.
      if (res.ok || res.status < 500 || res.status === 501) {
        reportBackendHealthy()
        return
      }
      if (res.status === 503 || res.status === 504) {
        reportBackendFailure({ status: res.status, message: res.statusText })
        return
      }
      // For other 5xx, do not flip global backend health; handle error locally.
      reportBackendHealthy()
    }
    markBackendStatusFromResponse()

    const text = await res.text()

    if (!text) {
      if (!res.ok && res.status !== 204) {
        const status = res.status || 500
        const message = res.statusText || "Request failed"
        throw new ApiError<T>(status, message)
      }
      return undefined as T
    }

    let json: unknown
    try {
      json = JSON.parse(text) as unknown
    } catch {
      const status = res.status || 500
      const message = res.statusText || "Invalid JSON response"
      throw new ApiError<T>(status, message)
    }

    if (json == null) {
      if (!res.ok) {
        const status = res.status || 500
        const message = res.statusText || "Request failed"
        throw new ApiError<T>(status, message)
      }
      return undefined as T
    }

    const isCommonResponse = (value: unknown): value is CommonResponse<T> =>
      typeof value === "object" &&
      value !== null &&
      "success" in value &&
      "statusCode" in value &&
      "message" in value &&
      "result" in value

    if (isCommonResponse(json)) {
      if (!res.ok || json.success === false) {
        const status = json.statusCode || res.status || 500
        const message = json.message || res.statusText || "Request failed"
        const payload = (json.result ?? null) as unknown as T
        throw new ApiError<T>(status, message, payload)
      }

      const result = json.result as T
      await this.offlineAdapter?.onSuccess(requestContext, result)
      return result
    }

    if (!res.ok) {
      const status = res.status || 500
      const message = res.statusText || "Request failed"
      throw new ApiError<T>(status, message, json as T)
    }

    const result = json as T
    await this.offlineAdapter?.onSuccess(requestContext, result)
    return result
  }

  get<T>(path: string) {
    return this.request<T>(path, { method: "GET" })
  }
  post<T, B = unknown>(path: string, body?: B) {
    return this.request<T>(path, { method: "POST", body: body as BodyInit })
  }
  put<T, B = unknown>(path: string, body?: B) {
    return this.request<T>(path, { method: "PUT", body: body as BodyInit })
  }
  patch<T, B = unknown>(path: string, body?: B) {
    return this.request<T>(path, { method: "PATCH", body: body as BodyInit })
  }
  delete<T>(path: string) {
    return this.request<T>(path, { method: "DELETE" })
  }
}

const trimTrailingSlash = (value?: string | null) => {
  if (!value) return ""
  return value.endsWith("/") ? value.replace(/\/+$/, "") : value
}

const DEFAULT_API_BASE_URL = "http://localhost:8081"

export const API_BASE_URL =
  trimTrailingSlash(import.meta.env.VITE_API_BASE_URL) || DEFAULT_API_BASE_URL

const baseClient = new HttpClient(
  API_BASE_URL,
  () => localStorage.getItem("auth_token")
)

baseClient.setOfflineAdapter(new OfflineHttpAdapter())

export const api = baseClient
